/* SSL fast add to cart — Agentcy for SickScience
   Loaded before theme.js, so this <product-form> is registered first and the theme skips its own.
   It keeps the theme's markup and events (theme:loading:start/end, cart:prepare-bundled-sections,
   variant:add on the form, cart:change on <html>, cart:error + cart:refresh on failure) and makes the add faster:
   - the cart drawer opens with an "Adding to your cart" state the moment the form is submitted;
   - the cart JSON comes back inside the add request (section "ssl-cart-json"),
     so the extra /cart.js round trip the theme made after every add is gone (falls back to it if missing). */
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const CART_JSON = 'ssl-cart-json';

const themeSettings = () => (window.themeVariables && window.themeVariables.settings) || {};

const STYLE = `
cart-drawer.ssl-is-adding > :not([slot]):not(.ssl-adding),
cart-drawer.ssl-is-adding > [slot="footer"] { opacity: .35; pointer-events: none; transition: opacity .15s; }
cart-drawer.ssl-is-adding--empty > :not([slot]):not(.ssl-adding) { display: none; }
.ssl-adding { display: flex; align-items: center; gap: 10px; margin: 0 0 14px; padding: 12px 14px; border-radius: 10px;
  background: rgba(169, 33, 192, .08); color: #a921c0; font-size: 14px; font-weight: 600; }
.ssl-adding__spin,
.ssl-btn[aria-busy="true"]::before,
.ssl-atc[aria-busy="true"]::before {
  content: ""; display: inline-block; flex: none; width: 15px; height: 15px; border: 2px solid currentColor;
  border-right-color: transparent; border-radius: 50%; animation: ssl-atc-spin .7s linear infinite; }
.ssl-btn[aria-busy="true"]::before,
.ssl-atc[aria-busy="true"]::before { margin-right: 8px; vertical-align: -3px; }
@keyframes ssl-atc-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) {
  .ssl-adding__spin, .ssl-btn[aria-busy="true"]::before, .ssl-atc[aria-busy="true"]::before { animation-duration: 2s; }
}`;

const injectStyle = () => {
  if (document.getElementById('ssl-fast-atc-style')) return;
  const style = document.createElement('style');
  style.id = 'ssl-fast-atc-style';
  style.textContent = STYLE;
  document.head.append(style);
};
if (document.head) injectStyle();

/* Forms inside a quick-buy modal or drawer keep the theme's flow (close that first, then open the cart). */
const insideDialog = (el) => {
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (node.tagName === 'DIALOG' || /MODAL|DRAWER|POPOVER/.test(node.tagName)) return true;
  }
  return false;
};

const clearAdding = (drawer) => {
  if (!drawer) return;
  drawer.classList.remove('ssl-is-adding', 'ssl-is-adding--empty');
  drawer.querySelectorAll(':scope > .ssl-adding').forEach((el) => el.remove());
};

const openDrawerEarly = (form) => {
  const settings = themeSettings();
  if (settings.cartType !== 'drawer' || settings.pageType === 'cart' || insideDialog(form)) return null;
  const drawer = document.querySelector('cart-drawer');
  if (!drawer || typeof drawer.show !== 'function') return null;
  injectStyle();
  clearAdding(drawer);
  const note = document.createElement('div');
  note.className = 'ssl-adding';
  note.setAttribute('role', 'status');
  note.innerHTML = '<span class="ssl-adding__spin" aria-hidden="true"></span><span>Adding to your cart…</span>';
  drawer.prepend(note);
  drawer.classList.add('ssl-is-adding');
  if (!drawer.querySelector('line-item, .line-item')) drawer.classList.add('ssl-is-adding--empty');
  try { drawer.show(); } catch (e) { clearAdding(drawer); return null; }
  return drawer;
};

const readCartJson = (sections) => {
  const html = sections && sections[CART_JSON];
  if (!html) return null;
  const match = html.match(/<script[^>]*data-ssl-cart-json[^>]*>([\s\S]*?)<\/script>/);
  if (!match) return null;
  try {
    const cart = JSON.parse(match[1]);
    return cart && Array.isArray(cart.items) ? cart : null;
  } catch (e) {
    return null;
  }
};

class SslProductForm extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const form = this.form;
    if (!form) return;
    form.addEventListener('submit', (event) => this.onSubmit(event), { signal: this.abortController.signal });
    const idInput = form.elements.namedItem('id');
    if (idInput && 'disabled' in idInput) idInput.disabled = false;
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  get form() {
    return this.querySelector('form[action*="/cart/add"]');
  }

  async onSubmit(event) {
    event.preventDefault();
    const form = this.form;
    if (!form || event.submitter?.getAttribute('aria-busy') === 'true') return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const html = document.documentElement;
    const buttons = Array.from(form.elements).filter((el) => el.type === 'submit');
    buttons.forEach((btn) => btn.setAttribute('aria-busy', 'true'));
    html.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const settings = themeSettings();
    const toCartPage = settings.cartType === 'page' || settings.pageType === 'cart';
    const drawer = toCartPage ? null : openDrawerEarly(form);

    const sections = [];
    html.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    if (!toCartPage && !sections.includes(CART_JSON) && sections.length < 5) sections.push(CART_JSON);

    const formData = new FormData(form);
    formData.set('sections', [...new Set(sections)].join(','));
    formData.set('sections_url', `${root}variants/${formData.get('id')}`);

    let response = null;
    let json = {};
    try {
      response = await fetch(`${root}cart/add.js`, {
        body: formData,
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      json = await response.json();
    } catch (err) {
      json = { description: 'Something went wrong. Please try again.' };
    }

    buttons.forEach((btn) => btn.removeAttribute('aria-busy'));
    html.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

    if (response && response.ok) {
      if (toCartPage) {
        window.location.href = `${root}cart`;
        return;
      }
      let cart = readCartJson(json.sections);
      if (!cart) {
        try {
          cart = await (await fetch(`${root}cart.js`)).json();
        } catch (err) {
          window.location.href = `${root}cart`;
          return;
        }
      }
      const bundled = { ...(json.sections || {}) };
      delete bundled[CART_JSON];
      cart.sections = bundled;
      const onSuccessDo = formData.get('on_success');
      const items = Object.prototype.hasOwnProperty.call(json, 'items') ? json.items : [json];
      form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items, cart, onSuccessDo } }));
      html.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', onSuccessDo, cart } }));
      clearAdding(drawer);
    } else {
      if (drawer) {
        clearAdding(drawer);
        try { drawer.hide(); } catch (e) { /* drawer already closed */ }
      }
      form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: json.description || json.message } }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }
}

if (!window.customElements.get('product-form')) window.customElements.define('product-form', SslProductForm);
